import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import {
  ChevronDownIcon, MagnifyingGlassIcon
} from '@heroicons/react/16/solid'
import React from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useUIStore } from '../../../stores/ui/uiStore'
import { CommandPaletteNames } from '../../base/baseCommandePalette/CommandPaletteNames'
import { LinkLogo } from './LinkLogo'
import { MenuLink } from './MenuLink'
import { LinkButton } from './linkButton'
import { contextMenu } from './utils'

interface DesktopMenuProps {
  menuItems: typeof contextMenu[0]['links']
  getAccountLink: string
  isCommercialLanding: boolean
}

export const DesktopMenu = ({
  menuItems,
  getAccountLink,
  isCommercialLanding,
}: DesktopMenuProps) => {
  const setIsCommandPalettesOpen = useUIStore(useShallow(state => state.openCommandPalettes))

  return (
    <div className="xl:w-[1115px] lg:w-[950px] mx-auto 2xl:w-[1320px] hidden md:block">
      <div className="flex items-center justify-between h-full py-2.5 px-1 md:px-0 md:py-3">
        <LinkLogo />

        <ul className='flex flex-row items-center space-x-4 text-primary'>
          {menuItems?.map(({
            title,
            link,
            isExternal = false,
            isFolder = false,
            links = [],
          }) => (
            <li key={title} className='hover:text-night hover:underline'>
              {isFolder ? (
                <Menu>
                  <MenuButton className="flex gap-x-2 items-center text-primary focus:outline-none focus:ring-0">
                    {title}
                    <ChevronDownIcon className="size-4 text-primary" />
                  </MenuButton>

                  <MenuItems
                    transition
                    anchor="bottom start"
                    className="z-50 p-1 w-52 bg-white rounded-xl transition duration-100 ease-out origin-top-right md:w-96 text-sm/6 text-primary data-closed:scale-95 data-closed:opacity-0 focus:outline-none focus:ring-0"
                  >
                    {links.map(({
                      title: childTitle,
                      link: childLink,
                      isExternal: childIsExternal,
                    }) => (
                      <MenuItem key={childTitle}>
                        <MenuLink
                          title={childTitle}
                          href={childLink || ''}
                          isExternal={childIsExternal || false}
                          label={childTitle}
                          className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-white/10"
                        />
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              ) : (
                <MenuLink
                  title={title}
                  href={link || ''}
                  isExternal={isExternal || false}
                  label={title}
                />
              )}
            </li>
          ))}
        </ul>

        <div className='flex flex-row justify-between items-center space-x-4'>
          <button
            type="button"
            onClick={() => setIsCommandPalettesOpen(CommandPaletteNames.SEARCH_FIRM_OFFER)}
            className="flex gap-x-2 justify-between items-center px-4 md:px-3 md:py-1 py-2 rounded-md border-[.5px] border-primary hover:bg-primary hover:text-white transition-all duration-300 ease-in-out"
          >
            <p className="sr-only">Rechercher une offre ou un cabinet</p>
            <MagnifyingGlassIcon className="w-4 h-4 md:w-5 md:h-5 text-primary hover:text-white" />
          </button>

          <LinkButton
            title={isCommercialLanding ? 'Accéder à l\'espace employeur' : 'Accéder à l\'espace candidat'}
            href={getAccountLink}
            label={isCommercialLanding ? 'Mon compte employeur' : 'Mon compte candidat'}
            isInverted
            size="small"
            isExternal
          />

          <LinkButton
            title={`Accéder à l'espace ${isCommercialLanding ? 'candidat' : 'employeur'}`}
            href={isCommercialLanding ? '/offres' : '/je-suis-un-cabinet'}
            label={isCommercialLanding ? 'Espace candidat' : 'Espace Employeur'}
            color='white'
            size="small"
          />
        </div>
      </div>
    </div>
  )
}
